import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { CriterionIn, CriterionOut } from "./types";

const KEY = ["criteria"] as const;

export function useCriteria() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<CriterionOut[]>("/criteria"),
  });
}

export function useCriterion(id: number | string | undefined) {
  return useQuery({
    queryKey: ["criterion", id],
    queryFn: () => api<CriterionOut>(`/criteria/${id}`),
    enabled: id !== undefined && id !== "",
  });
}

export function useCreateCriterion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CriterionIn) =>
      api<CriterionOut>("/criteria", { method: "POST", json: payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateCriterion(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CriterionIn) =>
      api<CriterionOut>(`/criteria/${id}`, { method: "PUT", json: payload }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: ["criterion", id] });
    },
  });
}

export function useToggleCriterion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<CriterionOut>(`/criteria/${id}/toggle`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteCriterion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/criteria/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
